import { useState, useCallback, useRef, useEffect } from 'react';
import { InputAdornment, TextField } from '@mui/material';

/**
 * CurrencyInput — A text input that auto-formats numbers with commas.
 * Stores the raw numeric value externally, displays formatted with commas.
 *
 * Props:
 *   value     — numeric value (number or string)
 *   onChange   — called with the raw numeric value (number) or '' for empty
 *   prefix     — optional prefix like '$' shown inside the input group
 *   disabled   — whether the input is disabled
 *   placeholder — placeholder text
 *   label      — optional MUI TextField label
 *   size       — MUI size ('small' | 'medium')
 *   decimalPlaces — optional; when set (e.g. 2), blur and external sync show exactly that many fractional digits
 *   onBlur     — forwarded after internal blur handling (e.g. RHF field.onBlur)
 *   ...rest    — passed through to MUI TextField
 */
const formatWithCommas = (val) => {
  if (val === '' || val == null) return '';
  const str = String(val);
  const parts = str.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

/** Formats a numeric value with commas and exactly `places` digits after the decimal (when places is set). */
const formatWithDecimalPlaces = (val, places) => {
  if (val === '' || val == null || val === undefined) return '';
  const num = typeof val === 'number' ? val : parseFloat(String(val).replace(/,/g, ''));
  if (Number.isNaN(num)) return '';
  const fixed = num.toFixed(places);
  const [intPart, frac] = fixed.split('.');
  const intWith = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return frac !== undefined ? `${intWith}.${frac}` : intWith;
};

const displayForValue = (value, decimalPlaces) => {
  if (value === '' || value == null) return '';
  if (decimalPlaces != null && decimalPlaces >= 0) {
    return formatWithDecimalPlaces(value, decimalPlaces);
  }
  return formatWithCommas(value);
};

const CurrencyInput = ({
  value,
  onChange,
  onBlur,
  prefix = '$',
  disabled = false,
  placeholder = '',
  label,
  size = 'small',
  /** When set (e.g. 2), show this many fractional digits after blur / external sync */
  decimalPlaces,
  ...rest
}) => {
  const [displayValue, setDisplayValue] = useState(() => displayForValue(value, decimalPlaces));
  const inputRef = useRef(null);
  const cursorRef = useRef(null);

  // Sync display when value changes externally (but not during typing)
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setDisplayValue(displayForValue(value, decimalPlaces));
    }
  }, [value, decimalPlaces]);

  const handleChange = useCallback((e) => {
    const raw = e.target.value;
    const cursorPos = e.target.selectionStart;

    // Strip everything except digits, dots, and minus
    const cleaned = raw.replace(/[^0-9.-]/g, '');

    // Prevent multiple dots
    const dotIdx = cleaned.indexOf('.');
    const sanitized = dotIdx >= 0
      ? cleaned.slice(0, dotIdx + 1) + cleaned.slice(dotIdx + 1).replace(/\./g, '')
      : cleaned;

    // Format with commas
    const formatted = formatWithCommas(sanitized);
    setDisplayValue(formatted);

    // Calculate cursor adjustment (commas added/removed)
    const commasBefore = (raw.slice(0, cursorPos).match(/,/g) || []).length;
    const commasAfter = (formatted.slice(0, cursorPos).match(/,/g) || []).length;
    cursorRef.current = cursorPos + (commasAfter - commasBefore);

    // Emit raw numeric value
    if (sanitized === '' || sanitized === '-') {
      onChange('');
    } else {
      const num = parseFloat(sanitized);
      onChange(isNaN(num) ? '' : num);
    }
  }, [onChange]);

  // Restore cursor position after React re-render
  useEffect(() => {
    if (cursorRef.current != null && inputRef.current === document.activeElement) {
      const pos = cursorRef.current;
      inputRef.current.setSelectionRange(pos, pos);
      cursorRef.current = null;
    }
  });

  const handleBlur = useCallback(
    (e) => {
      if (decimalPlaces != null && value !== '' && value != null && !Number.isNaN(Number(value))) {
        const n = Number(value);
        const rounded = Number(n.toFixed(decimalPlaces));
        onChange(rounded);
        setDisplayValue(formatWithDecimalPlaces(rounded, decimalPlaces));
      } else {
        setDisplayValue(displayForValue(value, decimalPlaces));
      }
      onBlur?.(e);
    },
    [value, decimalPlaces, onChange, onBlur],
  );

  return (
    <TextField
      inputRef={inputRef}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={placeholder}
      label={label}
      size={size}
      slotProps={{
        input: prefix ? { startAdornment: <InputAdornment position="start">{prefix}</InputAdornment> } : undefined,
      }}
      {...rest}
    />
  );
};

export default CurrencyInput;
